<template>
  <div class="p-6 space-y-4">
    <header>
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
        <i class="far fa-radar text-primary-500 mr-2" />
        Find Brand Competitors
      </h2>
      <p class="text-sm text-gray-500">
        We'll search Google Local for each selected service in
        <strong>{{ defaultLocation || 'your Location\'s city' }}</strong>
        and show you the competing businesses we find.
      </p>
      <div class="mt-3 rounded-md bg-primary-50 dark:bg-primary-900/20 p-3 text-xs text-primary-900 dark:text-primary-200">
        <p class="font-semibold mb-1">
          <i class="far fa-circle-info mr-1" />
          How this works
        </p>
        <ul class="list-disc ml-5 space-y-0.5">
          <li>Each selected service runs as <strong>one SerpAPI Google Local query</strong> (rate-limited, so keep selections focused).</li>
          <li>Results are <strong>not</strong> automatically added — you review each business before approving as a negative keyword.</li>
        </ul>
      </div>
    </header>

    <fieldset>
      <legend class="text-sm font-medium text-gray-900 dark:text-white">
        Services to scan
        <i v-tooltip="'Each checked service runs as one SerpAPI query. Uncheck services you do not want to spend a query on.'"
          class="far fa-circle-info text-primary-300 ml-1" />
      </legend>
      <div v-if="availableServices.length === 0" class="mt-2 text-sm text-gray-500">
        No services on this Location. Will fall back to the GBP category.
      </div>
      <div v-else class="mt-2 space-y-1">
        <label v-for="svc in availableServices" :key="svc" class="flex items-center gap-2">
          <input type="checkbox" :value="svc" v-model="selectedServices">
          <span class="text-sm">{{ svc }}</span>
        </label>
      </div>
    </fieldset>

    <label class="block">
      <span class="text-sm font-medium text-gray-900 dark:text-white">
        Location override (optional)
        <i v-tooltip="'Override the city/state used for the Google Local search. Leave blank to use the default city for this Location.'"
          class="far fa-circle-info text-primary-300 ml-1" />
      </span>
      <input
        v-model="serpapiLocation"
        type="text"
        :placeholder="defaultLocation || 'City, State'"
        class="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white">
    </label>

    <p v-if="error" class="text-sm text-red-600">
      {{ error }}
    </p>

    <div class="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
      <button type="button" class="btn btn-secondary btn-sm" @click="$emit('on-close-panel')">
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary btn-sm"
        :disabled="running || !canRun"
        @click="run">
        <UiSpinner v-if="running" :size="4" screen-reader="Starting" class="inline-block mr-2 align-middle" />
        {{ running ? 'Starting…' : 'Run Scan' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import vTooltip from '~/javascript/directives/tooltip'
import UiSpinner from '~/javascript/components/ui/spinner.vue'
import { toastSuccess, toastError } from '~/javascript/utils/toast'

const props = defineProps({
  locationId: { type: String, required: true },
  csrfToken: { type: String, required: true },
  availableServices: { type: Array, default: () => []},
  defaultLocation: { type: String, default: '' }
})

const emit = defineEmits(['scan-started', 'on-close-panel'])

const selectedServices = ref(props.availableServices.length > 0 ? [...props.availableServices] : [])
const serpapiLocation = ref('')
const running = ref(false)
const error = ref(null)

const canRun = computed(() => props.availableServices.length === 0 || selectedServices.value.length > 0)

const run = async() => {
  running.value = true
  error.value = null
  try {
    const res = await fetch(`/ajax/locations/${props.locationId}/competitor_scan_runs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' },
      body: JSON.stringify({
        services: selectedServices.value,
        serpapi_location: serpapiLocation.value || null
      })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data.errors || ['Could not start scan']).join(', '))
    }
    const data = await res.json()
    toastSuccess('Scan started — results will appear when ready.')
    emit('scan-started', data.scan_run)
    emit('on-close-panel')
  } catch (e) {
    error.value = e.message
    toastError(e)
  } finally {
    running.value = false
  }
}
</script>
